import { InventaireListResponse } from "@repo/types";
import { AxiosInstance } from "axios";
import { axiosInstance } from "../axios-instance";
import { ENDPOINTS } from "../endpoint";
import { BaseApiService } from "./base-api.service";

// Types for Travaux operations
export interface TravauxValidRequest {
  ids: number[];
}

export interface TravauxValidResponse {
  message: string;
  count: number;
}

export interface TravauxSearchParams {
  flag: string;
  term: string;
  page?: number;
  limit?: number;
  code_site?: string;
}

export interface MonthlyAlertStats {
  countAbattage: number;
  countWorkType: number;
  countVisite: number;
  totalAlerts: number;
  breakdown: {
    abattage: {
      count: number;
      percentage: number;
    };
    workType: {
      count: number;
      percentage: number;
    };
    visites: {
      count: number;
      percentage: number;
    };
  };
}

export interface TravauxStatistics {
  totalTravaux: number;
  totalAlertes: number;
  totalExamens: number;
  travauxByMonth: Array<{ month: string; count: number }>;
  alertesByMonth: Array<{ month: string; count: number }>;
}

export class TravauxService extends BaseApiService {
  constructor(axios: AxiosInstance = axiosInstance) {
    super(ENDPOINTS.TRAVAUX.BASE, axios);
  }

  /**
   * Validate work completion for selected inventaires
   */
  async validWork(request: TravauxValidRequest): Promise<{ data: TravauxValidResponse }> {
    const { data } = await this.axios.post(ENDPOINTS.TRAVAUX.VALID, request);
    return data;
  }

  /**
   * Get inventaires that need work (travaux)
   */
  async getInventaireTravauxNew(
    page = 1,
    limit = 10,
    code_site?: string
  ): Promise<InventaireListResponse> {
    const { data } = await this.axios.get(ENDPOINTS.TRAVAUX.INVENTAIRES, {
      params: { page, limit, code_site }
    });
    return data;
  }

  /**
   * Search inventaires that need work
   */
  async getInventaireTravauxNewSearch(params: TravauxSearchParams): Promise<InventaireListResponse> {
    const { data } = await this.axios.get(ENDPOINTS.TRAVAUX.INVENTAIRES_SEARCH, {
      params
    });
    return data;
  }

  /**
   * Get alert inventaires (work needed within current month)
   */
  async getAlertInventaireNew(page = 1, limit = 10): Promise<InventaireListResponse> {
    const { data } = await this.axios.get(ENDPOINTS.TRAVAUX.ALERTS, {
      params: { page, limit }
    });
    return data;
  }

  /**
   * Search alert inventaires
   */
  async getAlertInventaireNewSearch(params: TravauxSearchParams): Promise<InventaireListResponse> {
    const { data } = await this.axios.get(ENDPOINTS.TRAVAUX.ALERTS_SEARCH, {
      params
    });
    return data;
  }

  /**
   * Get monthly alert statistics with detailed breakdown
   */
  async getMonthlyAlertStats(
    startDate?: string,
    endDate?: string,
    code_site?: string,
    includeDrafts?: boolean
  ): Promise<{ data: MonthlyAlertStats }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (code_site) params.append('code_site', code_site);
    if (includeDrafts === true) params.append('includeDrafts', 'true');

    const { data } = await this.axios.get(
      `${ENDPOINTS.TRAVAUX.ALERTS_MONTHLY_STATS}${params.toString() ? `?${params.toString()}` : ''}`
    );
    return data;
  }

  /**
   * Get inventaires that need examens (complementary examinations)
   */
  async getInventaireExamens(): Promise<{ data: any[] }> {
    const { data } = await this.axios.get(ENDPOINTS.TRAVAUX.EXAMENS);
    return data;
  }

  /**
   * Get travaux statistics for dashboard
   */
  async getTravauxStatistics(startDate?: string, endDate?: string): Promise<{ data: TravauxStatistics }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const { data } = await this.axios.get(
      `${ENDPOINTS.TRAVAUX.STATISTICS}${params.toString() ? `?${params.toString()}` : ''}`
    );
    return data;
  }

  async getMonthlyBarChartData(year?: number, startDate?: string, endDate?: string): Promise<{
    data: {
      travaux: number[];
      visites: number[];
      months: string[];
    }
  }> {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const { data } = await this.axios.get(
      `${ENDPOINTS.TRAVAUX.MONTHLY_BAR_CHART}${params.toString() ? `?${params.toString()}` : ''}`
    );
    return data;
  }
}
